import { baseAPIUrl } from './constants/urls';

export interface APIError extends Error {
  status: number;
}

class API {
  private readonly baseURL: string;

  private token?: string | null;

  constructor(baseURL: string) {
    this.baseURL = baseURL;
    this.token = null;
  }

  authorize = (token?: string | null) => {
    this.token = token;
  };

  get = async (path: string, signal?: AbortSignal) => {
    return this.request(path, { signal, method: 'GET' });
  };

  post = async (path: string, body: Record<string, any>) => {
    return this.request(path, { method: 'POST', body: JSON.stringify(body) });
  };

  put = async (path: string, body: Record<string, any>) => {
    return this.request(path, { method: 'PUT', body: JSON.stringify(body) });
  };

  private readonly request = async (path: string, init: RequestInit) => {
    const headers: Record<string, string> = {
      Accept: 'application/json;version=1',
      'Content-Type': 'application/json',
    };

    if (this.token) {
      headers.Authorization = `Bearer ${this.token}`;
    }

    const response = await fetch(`${this.baseURL}${path}`, {
      ...init,
      headers,
    });

    if (response.ok) return this.parseJSON(response);

    const body = await this.parseJSON(response);
    const message = body?.error ?? body?.message ?? response.statusText;
    const err = new Error(message) as APIError;
    err.status = response.status;
    throw err;
  };

  private readonly parseJSON = async (response: Response) => {
    if (response.status === 204 || response.status === 205) return null;
    const contentType = response.headers.get('content-type') ?? '';
    if (!contentType.includes('application/json')) return null;
    return response.json();
  };
}

export const api = new API(baseAPIUrl);
